import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  ColorInput,
  Container,
  Divider,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Switch,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconTrash } from "@tabler/icons-react";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  clampTileHeight,
  chooseValidDoor,
  createDefaultLayoutForRoomType,
  listExteriorBoundaryEdges,
  normalizeLayoutWalls,
  toGridCell,
  toWorldCell,
} from "../../shared/room-layout";
import { createDefaultRoomThemeColors } from "../../shared/room-theme";
import type {
  DoorDirection,
  RoomLayoutAccess,
  RoomLayoutOption,
  RoomLayoutRuntime,
  RoomLayoutState,
  RoomLayoutTile,
  RoomLayoutWallEdge,
  RoomThemeAccess,
  RoomThemeColors,
  RoomThemeOption,
  RoomThemeRuntime,
  RoomThemeState,
} from "../../shared/types";
import { ROOM_TYPE_CONFIG } from "../../shared/types";
import {
  addAdmin,
  createAdminLayout,
  createAdminRoomTheme,
  fetchAdminLayouts,
  fetchAdminRoomThemes,
  fetchAdmins,
  removeAdmin,
  revokeLayoutUnlock,
  revokeThemeUnlock,
  unlockLayoutForAddress,
  unlockThemeForAddress,
  updateAdminLayout,
  updateAdminRoomTheme,
  type AdminAddress,
  type RoomLayoutInput,
  type RoomThemeInput,
  type Session,
} from "../lib/api";
import {
  LayoutEditorCanvas,
  type LayoutEditorDrawMode,
  type LayoutEditorGridCell,
} from "../components/LayoutEditorCanvas";
import { LayoutPreviewCanvas } from "../components/LayoutPreviewCanvas";
import { glassPanel } from "../lib/glass";

const MIN_SIZE = 3;
const MAX_SIZE = 31;
const THEME_COLOR_SWATCHES: string[] = [
  "#f0ebe3",
  "#f4f4f4",
  "#d8d8d8",
  "#c7c7c7",
  "#8db9ff",
  "#b9d7ff",
  "#6d9ee8",
  "#5e8cd4",
  "#7fdb9f",
  "#b9f2cd",
  "#58b67a",
  "#4aa46d",
  "#ffb184",
  "#ffd5bb",
  "#e38a5f",
  "#cf7449",
  "#7d7cff",
  "#a4a2ff",
  "#5652c9",
  "#4743ab",
];

interface Props {
  session: Session;
}

function resizeTiles(
  tiles: RoomLayoutTile[][],
  sourceWidth: number,
  sourceHeight: number,
  nextWidth: number,
  nextHeight: number
): RoomLayoutTile[][] {
  const createEmpty = (): RoomLayoutTile => ({
    walkable: false,
    height: 0,
  });
  const resized: RoomLayoutTile[][] = Array.from({ length: nextHeight }, () =>
    Array.from({ length: nextWidth }, () => createEmpty())
  );

  const maxRows = Math.min(sourceHeight, nextHeight, tiles.length);
  for (let row = 0; row < maxRows; row++) {
    const sourceRow = Array.isArray(tiles[row]) ? tiles[row] : [];
    const maxCols = Math.min(sourceWidth, nextWidth, sourceRow.length);
    for (let col = 0; col < maxCols; col++) {
      const current = sourceRow[col];
      if (!current) continue;
      resized[row][col] = {
        walkable: Boolean(current.walkable),
        height: clampTileHeight(current.height ?? 0),
        surface: current.surface,
      };
    }
  }
  return resized;
}

function remapWorldCellTopLeft(
  sourceLayout: { width: number; height: number },
  targetLayout: { width: number; height: number },
  x: number,
  z: number
): [number, number] | null {
  const sourceCell = toGridCell(sourceLayout, x, z);
  if (!sourceCell) {
    return null;
  }
  if (
    sourceCell.row < 0 ||
    sourceCell.row >= targetLayout.height ||
    sourceCell.col < 0 ||
    sourceCell.col >= targetLayout.width
  ) {
    return null;
  }
  return toWorldCell(targetLayout, sourceCell.row, sourceCell.col);
}

function formatTileHeight(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function wallEdgeKey(
  edge: Pick<RoomLayoutWallEdge, "x" | "z" | "direction">
): string {
  return `${edge.x},${edge.z},${edge.direction}`;
}

function toEditorState(layout?: RoomLayoutOption): RoomLayoutInput {
  if (!layout) {
    const shape = createDefaultLayoutForRoomType("home");
    const walls = listExteriorBoundaryEdges(shape).filter(
      (edge) => edge.direction === "north" || edge.direction === "west"
    );
    return {
      name: "New Layout",
      maxUsers: ROOM_TYPE_CONFIG.home.capacity,
      width: shape.width,
      height: shape.height,
      tiles: shape.tiles,
      walls,
      door: shape.door,
      state: "draft",
      access: "free",
    };
  }
  return {
    name: layout.name,
    maxUsers: layout.maxUsers,
    width: layout.width,
    height: layout.height,
    tiles: layout.tiles.map((row) => row.map((cell) => ({ ...cell }))),
    walls: (
      layout.walls ??
      listExteriorBoundaryEdges(layout).filter(
        (edge) => edge.direction === "north" || edge.direction === "west"
      )
    ).map((edge) => ({ ...edge })),
    door: { ...layout.door },
    state: layout.state,
    access: layout.access,
  };
}

function toThemeEditorState(theme?: RoomThemeOption): RoomThemeInput {
  if (!theme) {
    return {
      name: "New Theme",
      state: "draft",
      access: "free",
      colors: createDefaultRoomThemeColors(),
    };
  }
  return {
    name: theme.name,
    state: theme.state,
    access: theme.access,
    colors: {
      wall: theme.wall,
      floorTop: theme.floorTop,
      floorSide: theme.floorSide,
      porchMarker: theme.porchMarker,
    },
  };
}

export function AdminScreen({ session }: Props) {
  const [layouts, setLayouts] = useState<
    Array<RoomLayoutOption & { unlockCount: number }>
  >([]);
  const [themes, setThemes] = useState<
    Array<RoomThemeOption & { unlockCount: number }>
  >([]);
  const [admins, setAdmins] = useState<AdminAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);
  const [selectedLayoutId, setSelectedLayoutId] = useState<string>("new");
  const [selectedThemeId, setSelectedThemeId] = useState<string>("new");
  const [drawMode, setDrawMode] = useState<LayoutEditorDrawMode>("draw");
  const [wallDirection, setWallDirection] = useState<DoorDirection>("north");
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [adminAddressInput, setAdminAddressInput] = useState("");
  const [unlockAddressInput, setUnlockAddressInput] = useState("");
  const [themeUnlockAddressInput, setThemeUnlockAddressInput] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [themeUnlocking, setThemeUnlocking] = useState(false);
  const [themeRevoking, setThemeRevoking] = useState(false);

  const selectedLayout =
    selectedLayoutId === "new"
      ? null
      : layouts.find((layout) => layout.id === selectedLayoutId) ?? null;
  const selectedTheme =
    selectedThemeId === "new"
      ? null
      : themes.find((theme) => theme.id === selectedThemeId) ?? null;
  const [draft, setDraft] = useState<RoomLayoutInput>(() => toEditorState());
  const [themeDraft, setThemeDraft] = useState<RoomThemeInput>(() =>
    toThemeEditorState()
  );
  const doorCell = useMemo(
    () =>
      toGridCell(
        { width: draft.width, height: draft.height },
        draft.door.x,
        draft.door.z
      ),
    [draft.width, draft.height, draft.door.x, draft.door.z]
  );
  const doorTile = useMemo(
    () => (doorCell ? draft.tiles[doorCell.row]?.[doorCell.col] ?? null : null),
    [doorCell, draft.tiles]
  );
  const tileStats = useMemo(() => {
    let walkable = 0;
    let stairs = 0;
    let maxHeight = 0;
    for (const row of draft.tiles) {
      for (const tile of row) {
        if (!tile.walkable) continue;
        walkable += 1;
        if (tile.surface === "stairs") {
          stairs += 1;
        }
        if (tile.height > maxHeight) {
          maxHeight = tile.height;
        }
      }
    }
    return { walkable, stairs, maxHeight };
  }, [draft.tiles]);

  const reload = async () => {
    setLoading(true);
    try {
      const [layoutList, themeList, adminList] = await Promise.all([
        fetchAdminLayouts(session),
        fetchAdminRoomThemes(session),
        fetchAdmins(session),
      ]);
      setLayouts(layoutList);
      setThemes(themeList);
      setAdmins(adminList);
    } catch (error) {
      notifications.show({
        title: "Failed to load admin data",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [session.address]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDraft(toEditorState(selectedLayout ?? undefined));
  }, [selectedLayoutId, selectedLayout?.id, selectedLayout?.updatedAt]);

  useEffect(() => {
    setThemeDraft(toThemeEditorState(selectedTheme ?? undefined));
  }, [selectedThemeId, selectedTheme?.id, selectedTheme?.updatedAt]);

  const applyDraftShape = (next: {
    width?: number;
    height?: number;
    tiles?: RoomLayoutTile[][];
    door?: RoomLayoutInput["door"];
  }) => {
    setDraft((prev) => {
      const rawWidth =
        typeof next.width === "number" && Number.isFinite(next.width)
          ? Math.round(next.width)
          : prev.width;
      const rawHeight =
        typeof next.height === "number" && Number.isFinite(next.height)
          ? Math.round(next.height)
          : prev.height;
      const width = Math.max(MIN_SIZE, Math.min(MAX_SIZE, rawWidth));
      const height = Math.max(MIN_SIZE, Math.min(MAX_SIZE, rawHeight));
      const sourceTiles = next.tiles ?? prev.tiles;
      const sourceHeight = Array.isArray(sourceTiles)
        ? sourceTiles.length
        : prev.height;
      const sourceWidth = Array.isArray(sourceTiles[0])
        ? sourceTiles[0]?.length ?? prev.width
        : prev.width;
      const tiles = next.tiles
        ? resizeTiles(next.tiles, sourceWidth, sourceHeight, width, height)
        : resizeTiles(prev.tiles, prev.width, prev.height, width, height);
      const sourceLayout = { width: prev.width, height: prev.height };
      const targetLayout = { width, height };
      const candidateDoor = next.door ?? prev.door;
      const remappedDoorPosition = remapWorldCellTopLeft(
        sourceLayout,
        targetLayout,
        candidateDoor.x,
        candidateDoor.z
      );
      const doorCandidate = remappedDoorPosition
        ? {
            ...candidateDoor,
            x: remappedDoorPosition[0],
            z: remappedDoorPosition[1],
          }
        : candidateDoor;
      const normalizedDoor = chooseValidDoor(
        {
          width,
          height,
          tiles,
          door: doorCandidate,
        },
        doorCandidate
      );
      const remappedWalls = (prev.walls ?? [])
        .map((edge) => {
          const remappedPosition = remapWorldCellTopLeft(
            sourceLayout,
            targetLayout,
            edge.x,
            edge.z
          );
          if (!remappedPosition) return null;
          return {
            x: remappedPosition[0],
            z: remappedPosition[1],
            direction: edge.direction,
          } satisfies RoomLayoutWallEdge;
        })
        .filter((edge): edge is RoomLayoutWallEdge => Boolean(edge));
      const normalizedWalls = normalizeLayoutWalls(remappedWalls, {
        width,
        height,
        tiles,
      });
      return {
        ...prev,
        width,
        height,
        tiles,
        walls: normalizedWalls,
        door: normalizedDoor,
      };
    });
  };

  const defaultPreviewLayout = useMemo<RoomLayoutRuntime>(() => {
    const initial = toEditorState();
    return {
      id: "layout-preview-new",
      name: initial.name,
      access: initial.access,
      maxUsers: initial.maxUsers,
      width: initial.width,
      height: initial.height,
      tiles: initial.tiles,
      walls: initial.walls,
      door: initial.door,
    };
  }, []);

  const activeDraftPreview = useMemo<RoomLayoutRuntime>(
    () => ({
      id:
        selectedLayoutId === "new" ? "layout-preview-draft" : selectedLayoutId,
      name: draft.name.trim() || "Draft Layout",
      access: draft.access,
      maxUsers: draft.maxUsers,
      width: draft.width,
      height: draft.height,
      tiles: draft.tiles,
      walls: draft.walls,
      door: draft.door,
    }),
    [draft, selectedLayoutId]
  );
  const themePreviewLayout = useMemo<RoomLayoutRuntime>(() => {
    if (selectedLayout) {
      return {
        id: selectedLayout.id,
        name: selectedLayout.name,
        access: selectedLayout.access,
        maxUsers: selectedLayout.maxUsers,
        width: selectedLayout.width,
        height: selectedLayout.height,
        tiles: selectedLayout.tiles,
        walls: selectedLayout.walls,
        door: selectedLayout.door,
      };
    }
    return defaultPreviewLayout;
  }, [defaultPreviewLayout, selectedLayout]);
  const themePreviewRuntime = useMemo<RoomThemeRuntime>(
    () => ({
      id: selectedThemeId === "new" ? "theme-preview-draft" : selectedThemeId,
      name: themeDraft.name.trim() || "Draft Theme",
      access: themeDraft.access,
      wall: themeDraft.colors.wall,
      floorTop: themeDraft.colors.floorTop,
      floorSide: themeDraft.colors.floorSide,
      porchMarker: themeDraft.colors.porchMarker,
    }),
    [selectedThemeId, themeDraft]
  );

  const applyToolCells = (cells: LayoutEditorGridCell[]) => {
    if (cells.length === 0) return;
    setDraft((prev) => {
      const width = prev.width;
      const height = prev.height;
      const layoutShape = { width, height };
      const uniqueCells: LayoutEditorGridCell[] = [];
      const visited = new Set<string>();
      for (const cell of cells) {
        if (
          cell.row < 0 ||
          cell.row >= height ||
          cell.col < 0 ||
          cell.col >= width
        ) {
          continue;
        }
        const key = `${cell.row},${cell.col}`;
        if (visited.has(key)) continue;
        visited.add(key);
        uniqueCells.push(cell);
      }
      if (uniqueCells.length === 0) {
        return prev;
      }

      const nextTiles = prev.tiles.map((row) =>
        row.map((tile) => ({ ...tile }))
      );
      const wallMap = new Map<string, RoomLayoutWallEdge>();
      for (const edge of prev.walls ?? []) {
        wallMap.set(wallEdgeKey(edge), { ...edge });
      }

      let doorCandidate = { ...prev.door };
      for (const cell of uniqueCells) {
        const tile = nextTiles[cell.row]?.[cell.col];
        if (!tile) continue;

        if (drawMode === "draw") {
          nextTiles[cell.row][cell.col] = {
            ...tile,
            walkable: true,
            surface: "floor",
          };
          continue;
        }
        if (drawMode === "stairs") {
          nextTiles[cell.row][cell.col] = {
            ...tile,
            walkable: true,
            surface: "stairs",
          };
          continue;
        }
        if (drawMode === "erase") {
          nextTiles[cell.row][cell.col] = {
            walkable: false,
            height: 0,
          };
          continue;
        }
        if (drawMode === "raise") {
          nextTiles[cell.row][cell.col] = {
            ...tile,
            walkable: true,
            height: clampTileHeight(tile.height + 0.5),
            surface: tile.surface ?? "floor",
          };
          continue;
        }
        if (drawMode === "lower") {
          if (!tile.walkable) continue;
          nextTiles[cell.row][cell.col] = {
            ...tile,
            height: clampTileHeight(tile.height - 0.5),
            surface: tile.surface ?? "floor",
          };
          continue;
        }

        const [x, z] = toWorldCell(layoutShape, cell.row, cell.col);
        if (drawMode === "door") {
          if (!nextTiles[cell.row][cell.col]?.walkable) continue;
          doorCandidate = {
            x,
            z,
            direction: prev.door.direction,
          };
          continue;
        }
        if (drawMode === "wall") {
          if (!nextTiles[cell.row][cell.col]?.walkable) continue;
          const edge: RoomLayoutWallEdge = {
            x,
            z,
            direction: wallDirection,
          };
          const key = wallEdgeKey(edge);
          if (wallMap.has(key)) {
            wallMap.delete(key);
          } else {
            wallMap.set(key, edge);
          }
        }
      }

      const nextDoor = chooseValidDoor(
        {
          width,
          height,
          tiles: nextTiles,
          door: doorCandidate,
        },
        doorCandidate
      );
      const nextWalls = normalizeLayoutWalls(Array.from(wallMap.values()), {
        width,
        height,
        tiles: nextTiles,
      });

      return {
        ...prev,
        tiles: nextTiles,
        walls: nextWalls,
        door: nextDoor,
      };
    });
  };

  const saveLayout = async () => {
    setSavingLayout(true);
    try {
      if (!draft.name.trim()) {
        throw new Error("Layout name is required.");
      }
      if (selectedLayoutId === "new") {
        const created = await createAdminLayout(session, draft);
        notifications.show({
          title: "Layout created",
          message: created.name,
          color: "teal",
        });
        await reload();
        setSelectedLayoutId(created.id);
      } else {
        const updated = await updateAdminLayout(
          session,
          selectedLayoutId,
          draft
        );
        notifications.show({
          title: "Layout updated",
          message: updated.name,
          color: "teal",
        });
        await reload();
      }
    } catch (error) {
      notifications.show({
        title: "Could not save layout",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setSavingLayout(false);
    }
  };

  const saveTheme = async () => {
    setSavingTheme(true);
    try {
      const name = themeDraft.name.trim().slice(0, 48);
      if (!name) {
        throw new Error("Theme name is required.");
      }
      const payload: RoomThemeInput = {
        ...themeDraft,
        name,
      };
      if (selectedThemeId === "new") {
        const created = await createAdminRoomTheme(session, payload);
        notifications.show({
          title: "Theme created",
          message: created.name,
          color: "teal",
        });
        await reload();
        setSelectedThemeId(created.id);
      } else {
        const updated = await updateAdminRoomTheme(
          session,
          selectedThemeId,
          payload
        );
        notifications.show({
          title: "Theme updated",
          message: updated.name,
          color: "teal",
        });
        await reload();
      }
    } catch (error) {
      notifications.show({
        title: "Could not save theme",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setSavingTheme(false);
    }
  };

  const grantUnlock = async () => {
    if (!selectedLayout || !unlockAddressInput.trim()) return;
    setUnlocking(true);
    try {
      await unlockLayoutForAddress(
        session,
        selectedLayout.id,
        unlockAddressInput
      );
      setUnlockAddressInput("");
      notifications.show({
        title: "Unlock granted",
        message: `${selectedLayout.name} unlocked for ${unlockAddressInput}`,
        color: "teal",
      });
      await reload();
    } catch (error) {
      notifications.show({
        title: "Unlock failed",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setUnlocking(false);
    }
  };

  const revokeUnlock = async () => {
    if (!selectedLayout || !unlockAddressInput.trim()) return;
    setRevoking(true);
    try {
      await revokeLayoutUnlock(session, selectedLayout.id, unlockAddressInput);
      notifications.show({
        title: "Unlock revoked",
        message: `${selectedLayout.name} access removed from ${unlockAddressInput}`,
        color: "orange",
      });
      setUnlockAddressInput("");
      await reload();
    } catch (error) {
      notifications.show({
        title: "Revoke failed",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setRevoking(false);
    }
  };

  const grantThemeUnlock = async () => {
    if (!selectedTheme || !themeUnlockAddressInput.trim()) return;
    const target = themeUnlockAddressInput.trim();
    setThemeUnlocking(true);
    try {
      await unlockThemeForAddress(session, selectedTheme.id, target);
      setThemeUnlockAddressInput("");
      notifications.show({
        title: "Theme unlock granted",
        message: `${selectedTheme.name} unlocked for ${target}`,
        color: "teal",
      });
      await reload();
    } catch (error) {
      notifications.show({
        title: "Theme unlock failed",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setThemeUnlocking(false);
    }
  };

  const revokeThemeAccess = async () => {
    if (!selectedTheme || !themeUnlockAddressInput.trim()) return;
    const target = themeUnlockAddressInput.trim();
    setThemeRevoking(true);
    try {
      await revokeThemeUnlock(session, selectedTheme.id, target);
      setThemeUnlockAddressInput("");
      notifications.show({
        title: "Theme unlock revoked",
        message: `${selectedTheme.name} access removed from ${target}`,
        color: "orange",
      });
      await reload();
    } catch (error) {
      notifications.show({
        title: "Theme revoke failed",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setThemeRevoking(false);
    }
  };

  const addAdminAddress = async () => {
    if (!adminAddressInput.trim()) return;
    setAddingAdmin(true);
    try {
      await addAdmin(session, adminAddressInput);
      setAdminAddressInput("");
      notifications.show({
        title: "Admin added",
        message: "Role granted successfully.",
        color: "teal",
      });
      await reload();
    } catch (error) {
      notifications.show({
        title: "Could not add admin",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setAddingAdmin(false);
    }
  };

  return (
    <Box h="100%" style={{ overflowY: "auto" }}>
      <Container size="lg" py="xl">
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Text fw={700} size="xl" c="dark">
              Admin Control
            </Text>
            <Badge color={session.isAdmin ? "teal" : "gray"} variant="light">
              {session.isAdmin ? "Admin" : "Read Only"}
            </Badge>
          </Group>

          <Tabs defaultValue="layouts" keepMounted={false}>
            <Tabs.List grow>
              <Tabs.Tab value="layouts">Layouts</Tabs.Tab>
              <Tabs.Tab value="themes">Themes</Tabs.Tab>
              <Tabs.Tab value="admins">Admins</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="admins" pt="md">
              <Card p="md" radius="md" style={glassPanel}>
                <Stack gap="sm">
                  <Text fw={600} c="dark">
                    Admin Roles
                  </Text>
                  <Group align="end">
                    <TextInput
                      label="Grant admin to wallet"
                      placeholder="tz1..."
                      value={adminAddressInput}
                      onChange={(e) =>
                        setAdminAddressInput(e.currentTarget.value)
                      }
                      style={{ flex: 1 }}
                    />
                    <Button
                      loading={addingAdmin}
                      onClick={() => void addAdminAddress()}
                    >
                      Add Admin
                    </Button>
                  </Group>
                  <Stack gap={6}>
                    {admins.map((admin) => (
                      <Group key={admin.address} justify="space-between">
                        <Text size="sm" ff="monospace" c="gray.7">
                          {admin.address}
                        </Text>
                        {admin.address.toLowerCase() !==
                          session.address.toLowerCase() && (
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={async () => {
                              try {
                                await removeAdmin(session, admin.address);
                                notifications.show({
                                  title: "Admin removed",
                                  message: admin.address,
                                  color: "orange",
                                });
                                await reload();
                              } catch (error) {
                                notifications.show({
                                  title: "Could not remove admin",
                                  message:
                                    error instanceof Error
                                      ? error.message
                                      : "Please retry.",
                                  color: "red",
                                });
                              }
                            }}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        )}
                      </Group>
                    ))}
                    {admins.length === 0 && (
                      <Text size="sm" c="gray.5">
                        No admins configured yet.
                      </Text>
                    )}
                  </Stack>
                </Stack>
              </Card>
            </Tabs.Panel>

            <Tabs.Panel value="layouts" pt="md">
              <Card p="md" radius="md" style={glassPanel}>
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <Text fw={600} c="dark">
                      Room Layouts
                    </Text>
                    <Badge color={loading ? "yellow" : "gray"} variant="light">
                      {loading ? "Refreshing..." : `${layouts.length} layouts`}
                    </Badge>
                  </Group>
                  <Stack gap="xs">
                    <Text size="sm" c="gray.7" fw={600}>
                      Select Layout
                    </Text>
                    <Box
                      style={{
                        maxHeight: 420,
                        overflowY: "auto",
                        overflowX: "hidden",
                        paddingRight: 4,
                      }}
                    >
                      <Box
                        style={{
                          display: "grid",
                          gap: 10,
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(220px, 1fr))",
                        }}
                      >
                        <Card
                          p="xs"
                          radius="md"
                          style={{
                            cursor: "pointer",
                            border:
                              selectedLayoutId === "new"
                                ? "2px solid #ff922b"
                                : "1px solid rgba(0,0,0,0.08)",
                            background:
                              selectedLayoutId === "new"
                                ? "rgba(255, 146, 43, 0.06)"
                                : "#ffffff",
                          }}
                          onClick={() => setSelectedLayoutId("new")}
                        >
                          <Stack gap={8}>
                            <LayoutPreviewCanvas
                              layout={defaultPreviewLayout}
                              height={124}
                            />
                            <Group justify="space-between" align="center">
                              <Text fw={600} c="dark" size="sm">
                                New Layout
                              </Text>
                              <Badge size="xs" variant="light" color="blue">
                                Draft
                              </Badge>
                            </Group>
                          </Stack>
                        </Card>
                        {layouts.map((layout) => (
                          <Card
                            key={layout.id}
                            p="xs"
                            radius="md"
                            style={{
                              cursor: "pointer",
                              border:
                                selectedLayoutId === layout.id
                                  ? "2px solid #ff922b"
                                  : "1px solid rgba(0,0,0,0.08)",
                              background:
                                selectedLayoutId === layout.id
                                  ? "rgba(255, 146, 43, 0.06)"
                                  : "#ffffff",
                            }}
                            onClick={() => setSelectedLayoutId(layout.id)}
                          >
                            <Stack gap={8}>
                              <LayoutPreviewCanvas
                                layout={layout as RoomLayoutRuntime}
                                height={124}
                              />
                              <Text fw={600} c="dark" size="sm" lineClamp={1}>
                                {layout.name}
                              </Text>
                              <Group gap={6}>
                                <Badge size="xs" variant="light" color="gray">
                                  {layout.state}
                                </Badge>
                                <Badge size="xs" variant="light" color="violet">
                                  {layout.access}
                                </Badge>
                                <Badge size="xs" variant="light" color="teal">
                                  {layout.maxUsers} users
                                </Badge>
                              </Group>
                            </Stack>
                          </Card>
                        ))}
                      </Box>
                    </Box>
                  </Stack>
                  <Group grow>
                    <TextInput
                      label="Name"
                      value={draft.name}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          name: e.currentTarget.value,
                        }))
                      }
                    />
                    <Select
                      label="State"
                      value={draft.state}
                      data={[
                        { value: "draft", label: "Draft" },
                        { value: "published", label: "Published" },
                        { value: "archived", label: "Archived" },
                      ]}
                      onChange={(value) =>
                        setDraft((prev) => ({
                          ...prev,
                          state: (value as RoomLayoutState) ?? prev.state,
                        }))
                      }
                    />
                    <Select
                      label="Access"
                      value={draft.access}
                      data={[
                        { value: "free", label: "Free" },
                        { value: "premium", label: "Premium" },
                        { value: "unlockable", label: "Unlock Required" },
                      ]}
                      onChange={(value) =>
                        setDraft((prev) => ({
                          ...prev,
                          access: (value as RoomLayoutAccess) ?? prev.access,
                        }))
                      }
                    />
                    <NumberInput
                      label="Max Users"
                      min={1}
                      max={80}
                      value={draft.maxUsers}
                      onChange={(value) =>
                        setDraft((prev) => ({
                          ...prev,
                          maxUsers:
                            typeof value === "number" && Number.isFinite(value)
                              ? Math.max(1, Math.min(80, Math.round(value)))
                              : prev.maxUsers,
                        }))
                      }
                    />
                  </Group>

                  <Group grow>
                    <NumberInput
                      label="Width"
                      min={MIN_SIZE}
                      max={MAX_SIZE}
                      value={draft.width}
                      onChange={(value) =>
                        applyDraftShape({
                          width:
                            typeof value === "number"
                              ? value
                              : Number.parseInt(String(value), 10),
                        })
                      }
                    />
                    <NumberInput
                      label="Height"
                      min={MIN_SIZE}
                      max={MAX_SIZE}
                      value={draft.height}
                      onChange={(value) =>
                        applyDraftShape({
                          height:
                            typeof value === "number"
                              ? value
                              : Number.parseInt(String(value), 10),
                        })
                      }
                    />
                    <Select
                      label="Door Direction"
                      value={draft.door.direction}
                      data={[
                        { value: "west", label: "West" },
                        { value: "north", label: "North" },
                        { value: "east", label: "East" },
                        { value: "south", label: "South" },
                      ]}
                      onChange={(value) =>
                        applyDraftShape({
                          door: {
                            ...draft.door,
                            direction:
                              (value as "north" | "south" | "east" | "west") ??
                              draft.door.direction,
                          },
                        })
                      }
                    />
                  </Group>

                  <Stack gap={8}>
                    <Text size="sm" c="gray.7" fw={600}>
                      Editor Tools
                    </Text>
                    <SegmentedControl
                      value={drawMode}
                      onChange={(value) =>
                        setDrawMode(value as LayoutEditorDrawMode)
                      }
                      data={[
                        { value: "draw", label: "Paint Floor" },
                        { value: "stairs", label: "Paint Stairs" },
                        { value: "erase", label: "Erase" },
                        { value: "raise", label: "Raise" },
                        { value: "lower", label: "Lower" },
                        { value: "door", label: "Place Door" },
                        { value: "wall", label: "Toggle Wall" },
                      ]}
                    />
                    {drawMode === "wall" && (
                      <Select
                        label="Wall Side"
                        value={wallDirection}
                        data={[
                          { value: "north", label: "North (top edge)" },
                          { value: "west", label: "West (left edge)" },
                        ]}
                        onChange={(value) =>
                          setWallDirection((value as DoorDirection) ?? "north")
                        }
                      />
                    )}
                    <Group gap="xs" wrap="wrap">
                      <Badge color="orange" variant="light">
                        Door ({draft.door.x}, {draft.door.z}){" "}
                        {draft.door.direction}
                      </Badge>
                      <Badge color="yellow" variant="light">
                        Door height {formatTileHeight(doorTile?.height ?? 0)}
                      </Badge>
                      <Badge
                        color={
                          doorTile?.surface === "stairs" ? "orange" : "blue"
                        }
                        variant="light"
                      >
                        {doorTile?.surface === "stairs"
                          ? "Door on stairs"
                          : "Door on floor"}
                      </Badge>
                      <Badge color="teal" variant="light">
                        {tileStats.walkable} walkable
                      </Badge>
                      <Badge color="orange" variant="light">
                        {tileStats.stairs} stair tiles
                      </Badge>
                      <Badge color="grape" variant="light">
                        max h {formatTileHeight(tileStats.maxHeight)}
                      </Badge>
                    </Group>
                    <Text size="xs" c="gray.5">
                      Each square shows its exact level number. D marks the door
                      tile and S marks stair-surface tiles.
                    </Text>
                    <Text size="xs" c="gray.5">
                      Raise/Lower adjust by 0.5 so stair tiles can sit between
                      full floor levels.
                    </Text>
                    <Text size="xs" c="gray.5">
                      Drag across the canvas to paint continuously.
                    </Text>
                  </Stack>

                  <Box
                    style={{
                      display: "grid",
                      gap: 14,
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(320px, 1fr))",
                      alignItems: "start",
                    }}
                  >
                    <Stack gap="xs">
                      <Group gap="xs" wrap="wrap">
                        <Group gap={6} wrap="nowrap">
                          <Box
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              background: "hsl(190deg 58% 40%)",
                            }}
                          />
                          <Text size="xs" c="gray.6">
                            Walkable floor
                          </Text>
                        </Group>
                        <Group gap={6} wrap="nowrap">
                          <Box
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              background:
                                "repeating-linear-gradient(135deg, rgba(255,255,255,0.22) 0 3px, rgba(0,0,0,0.16) 3px 6px), hsl(34deg 70% 48%)",
                            }}
                          />
                          <Text size="xs" c="gray.6">
                            Stairs surface
                          </Text>
                        </Group>
                        <Group gap={6} wrap="nowrap">
                          <Box
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              border: "2px solid rgba(255,183,112,0.98)",
                              background: "hsl(26deg 92% 60%)",
                            }}
                          />
                          <Text size="xs" c="gray.6">
                            Door tile + direction
                          </Text>
                        </Group>
                        <Group gap={6} wrap="nowrap">
                          <Box
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              borderTop: "3px solid rgba(255,242,185,0.98)",
                              borderLeft: "3px solid rgba(255,242,185,0.98)",
                              background: "rgba(30,34,41,0.9)",
                            }}
                          />
                          <Text size="xs" c="gray.6">
                            Manual wall edges
                          </Text>
                        </Group>
                      </Group>
                      <LayoutEditorCanvas
                        width={draft.width}
                        height={draft.height}
                        tiles={draft.tiles}
                        walls={draft.walls}
                        door={draft.door}
                        drawMode={drawMode}
                        wallDirection={wallDirection}
                        onPaintCells={applyToolCells}
                      />
                    </Stack>

                    <Stack gap="xs">
                      <Text size="sm" fw={600} c="gray.7">
                        Live 3D Preview
                      </Text>
                      <LayoutPreviewCanvas
                        layout={activeDraftPreview}
                        height={360}
                        zoomMultiplier={1.75}
                      />
                      <Text size="xs" c="gray.5">
                        This preview updates while you paint. Door opening and
                        wall placement are rendered directly from the current
                        draft.
                      </Text>
                    </Stack>
                  </Box>

                  <Group justify="space-between">
                    <Button
                      variant="light"
                      onClick={() => setSelectedLayoutId("new")}
                    >
                      New Layout
                    </Button>
                    <Button
                      loading={savingLayout}
                      onClick={() => void saveLayout()}
                    >
                      {selectedLayoutId === "new"
                        ? "Create Layout"
                        : "Save Changes"}
                    </Button>
                  </Group>

                  {selectedLayout && selectedLayout.access !== "free" && (
                    <>
                      <Divider />
                      <Stack gap="xs">
                        <Text fw={600} c="dark">
                          Unlock Management
                        </Text>
                        <TextInput
                          label="Wallet address"
                          placeholder="tz1..."
                          value={unlockAddressInput}
                          onChange={(e) =>
                            setUnlockAddressInput(e.currentTarget.value)
                          }
                        />
                        <Group>
                          <Button
                            size="xs"
                            loading={unlocking}
                            onClick={() => void grantUnlock()}
                          >
                            Grant Unlock
                          </Button>
                          <Button
                            size="xs"
                            variant="light"
                            color="orange"
                            loading={revoking}
                            onClick={() => void revokeUnlock()}
                          >
                            Revoke Unlock
                          </Button>
                        </Group>
                        <Text size="xs" c="gray.5">
                          Current unlock count: {selectedLayout.unlockCount}
                        </Text>
                      </Stack>
                    </>
                  )}
                </Stack>
              </Card>
            </Tabs.Panel>

            <Tabs.Panel value="themes" pt="md">
              <Card p="md" radius="md" style={glassPanel}>
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <Text fw={600} c="dark">
                      Theme Library
                    </Text>
                    <Badge color={loading ? "yellow" : "gray"} variant="light">
                      {loading ? "Refreshing..." : `${themes.length} themes`}
                    </Badge>
                  </Group>

                  <Text size="xs" c="gray.5">
                    Wallpaper and floor are fully separate at runtime: each room
                    stores both a `wallThemeId` and `floorThemeId`.
                  </Text>

                  <Box
                    style={{
                      display: "grid",
                      gap: 14,
                      gridTemplateColumns:
                        "minmax(280px, 0.9fr) minmax(360px, 1.1fr)",
                      alignItems: "start",
                    }}
                  >
                    <Stack gap="xs">
                      <Text size="sm" c="gray.7" fw={600}>
                        Select Theme
                      </Text>
                      <Box
                        style={{
                          maxHeight: 560,
                          overflowY: "auto",
                          overflowX: "hidden",
                          paddingRight: 4,
                        }}
                      >
                        <Box
                          style={{
                            display: "grid",
                            gap: 10,
                            gridTemplateColumns:
                              "repeat(auto-fill, minmax(220px, 1fr))",
                          }}
                        >
                          <Card
                            p="xs"
                            radius="md"
                            style={{
                              cursor: "pointer",
                              border:
                                selectedThemeId === "new"
                                  ? "2px solid #ff922b"
                                  : "1px solid rgba(0,0,0,0.08)",
                              background:
                                selectedThemeId === "new"
                                  ? "rgba(255, 146, 43, 0.06)"
                                  : "#ffffff",
                            }}
                            onClick={() => setSelectedThemeId("new")}
                          >
                            <Stack gap={8}>
                              <Group gap={6}>
                                <Box
                                  style={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: 4,
                                    border: "1px solid rgba(0,0,0,0.12)",
                                    background: themeDraft.colors.wall,
                                  }}
                                  title={`Wall ${themeDraft.colors.wall}`}
                                />
                                <Box
                                  style={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: 4,
                                    border: "1px solid rgba(0,0,0,0.12)",
                                    background: themeDraft.colors.floorTop,
                                  }}
                                  title={`Floor top ${themeDraft.colors.floorTop}`}
                                />
                                <Box
                                  style={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: 4,
                                    border: "1px solid rgba(0,0,0,0.12)",
                                    background: themeDraft.colors.floorSide,
                                  }}
                                  title={`Floor side ${themeDraft.colors.floorSide}`}
                                />
                              </Group>
                              <Text fw={600} c="dark" size="sm">
                                New Theme
                              </Text>
                              <Badge size="xs" variant="light" color="blue">
                                Draft
                              </Badge>
                            </Stack>
                          </Card>

                          {themes.map((theme) => (
                            <Card
                              key={theme.id}
                              p="xs"
                              radius="md"
                              style={{
                                cursor: "pointer",
                                border:
                                  selectedThemeId === theme.id
                                    ? "2px solid #ff922b"
                                    : "1px solid rgba(0,0,0,0.08)",
                                background:
                                  selectedThemeId === theme.id
                                    ? "rgba(255, 146, 43, 0.06)"
                                    : "#ffffff",
                              }}
                              onClick={() => setSelectedThemeId(theme.id)}
                            >
                              <Stack gap={8}>
                                <Text fw={600} c="dark" size="sm" lineClamp={1}>
                                  {theme.name}
                                </Text>
                                <Group gap={6} wrap="wrap">
                                  <Badge size="xs" variant="light" color="gray">
                                    {theme.state}
                                  </Badge>
                                  <Badge
                                    size="xs"
                                    variant="light"
                                    color="violet"
                                  >
                                    {theme.access}
                                  </Badge>
                                </Group>
                                <Group gap={6}>
                                  <Box
                                    style={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: 4,
                                      border: "1px solid rgba(0,0,0,0.12)",
                                      background: theme.wall,
                                    }}
                                    title={`Wall ${theme.wall}`}
                                  />
                                  <Box
                                    style={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: 4,
                                      border: "1px solid rgba(0,0,0,0.12)",
                                      background: theme.floorTop,
                                    }}
                                    title={`Floor top ${theme.floorTop}`}
                                  />
                                  <Box
                                    style={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: 4,
                                      border: "1px solid rgba(0,0,0,0.12)",
                                      background: theme.floorSide,
                                    }}
                                    title={`Floor side ${theme.floorSide}`}
                                  />
                                  <Box
                                    style={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: 4,
                                      border: "1px solid rgba(0,0,0,0.12)",
                                      background: theme.porchMarker,
                                    }}
                                    title={`Porch marker ${theme.porchMarker}`}
                                  />
                                </Group>
                              </Stack>
                            </Card>
                          ))}
                        </Box>
                      </Box>

                      {selectedTheme && selectedTheme.access !== "free" && (
                        <Card p="sm" radius="md" style={glassPanel}>
                          <Stack gap="xs">
                            <Text fw={600} c="dark">
                              Theme Unlock Management
                            </Text>
                            <TextInput
                              label="Wallet address"
                              placeholder="tz1..."
                              value={themeUnlockAddressInput}
                              onChange={(event) =>
                                setThemeUnlockAddressInput(
                                  event.currentTarget.value
                                )
                              }
                            />
                            <Group>
                              <Button
                                size="xs"
                                loading={themeUnlocking}
                                onClick={() => void grantThemeUnlock()}
                              >
                                Grant Unlock
                              </Button>
                              <Button
                                size="xs"
                                variant="light"
                                color="orange"
                                loading={themeRevoking}
                                onClick={() => void revokeThemeAccess()}
                              >
                                Revoke Unlock
                              </Button>
                            </Group>
                            <Text size="xs" c="gray.5">
                              Current unlock count: {selectedTheme.unlockCount}
                            </Text>
                          </Stack>
                        </Card>
                      )}
                    </Stack>

                    <Stack gap="md">
                      <Group grow>
                        <TextInput
                          label="Name"
                          value={themeDraft.name}
                          onChange={(event) =>
                            setThemeDraft((prev) => ({
                              ...prev,
                              name: event.currentTarget.value,
                            }))
                          }
                        />
                        <Select
                          label="State"
                          value={themeDraft.state}
                          data={[
                            { value: "draft", label: "Draft" },
                            { value: "published", label: "Published" },
                            { value: "archived", label: "Archived" },
                          ]}
                          onChange={(value) =>
                            setThemeDraft((prev) => ({
                              ...prev,
                              state: (value as RoomThemeState) ?? prev.state,
                            }))
                          }
                        />
                        <Select
                          label="Access"
                          value={themeDraft.access}
                          data={[
                            { value: "free", label: "Free" },
                            { value: "premium", label: "Premium" },
                            { value: "unlockable", label: "Unlock Required" },
                          ]}
                          onChange={(value) =>
                            setThemeDraft((prev) => ({
                              ...prev,
                              access: (value as RoomThemeAccess) ?? prev.access,
                            }))
                          }
                        />
                      </Group>

                      <Box
                        style={{
                          display: "grid",
                          gap: 12,
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(260px, 1fr))",
                        }}
                      >
                        <Card
                          p="sm"
                          radius="md"
                          style={{
                            border: "1px solid rgba(0,0,0,0.08)",
                            background: "#f8f9fa",
                          }}
                        >
                          <Stack gap="xs">
                            <Text size="sm" fw={600} c="gray.7">
                              Wallpaper Palette
                            </Text>
                            <ColorInput
                              label="Wall Color"
                              value={themeDraft.colors.wall}
                              format="hex"
                              swatches={THEME_COLOR_SWATCHES}
                              onChange={(value) =>
                                setThemeDraft((prev) => ({
                                  ...prev,
                                  colors: {
                                    ...prev.colors,
                                    wall: value,
                                  },
                                }))
                              }
                            />
                            <Group gap={8}>
                              <Text size="xs" c="gray.5">
                                Live wall:
                              </Text>
                              <Box
                                style={{
                                  width: 36,
                                  height: 20,
                                  borderRadius: 4,
                                  border: "1px solid rgba(0,0,0,0.12)",
                                  background: themeDraft.colors.wall,
                                }}
                              />
                              <Text size="xs" ff="monospace" c="gray.6">
                                {themeDraft.colors.wall}
                              </Text>
                            </Group>
                          </Stack>
                        </Card>

                        <Card
                          p="sm"
                          radius="md"
                          style={{
                            border: "1px solid rgba(0,0,0,0.08)",
                            background: "#f8f9fa",
                          }}
                        >
                          <Stack gap="xs">
                            <Text size="sm" fw={600} c="gray.7">
                              Floor Palette
                            </Text>
                            <ColorInput
                              label="Floor Top"
                              value={themeDraft.colors.floorTop}
                              format="hex"
                              swatches={THEME_COLOR_SWATCHES}
                              onChange={(value) =>
                                setThemeDraft((prev) => ({
                                  ...prev,
                                  colors: {
                                    ...prev.colors,
                                    floorTop: value,
                                  },
                                }))
                              }
                            />
                            <ColorInput
                              label="Floor Side"
                              value={themeDraft.colors.floorSide}
                              format="hex"
                              swatches={THEME_COLOR_SWATCHES}
                              onChange={(value) =>
                                setThemeDraft((prev) => ({
                                  ...prev,
                                  colors: {
                                    ...prev.colors,
                                    floorSide: value,
                                  },
                                }))
                              }
                            />
                            <ColorInput
                              label="Porch Marker"
                              value={themeDraft.colors.porchMarker}
                              format="hex"
                              swatches={THEME_COLOR_SWATCHES}
                              onChange={(value) =>
                                setThemeDraft((prev) => ({
                                  ...prev,
                                  colors: {
                                    ...prev.colors,
                                    porchMarker: value,
                                  },
                                }))
                              }
                            />
                          </Stack>
                        </Card>
                      </Box>

                      <Stack gap="xs">
                        <Text size="sm" fw={600} c="gray.7">
                          Live 3D Theme Preview
                        </Text>
                        <LayoutPreviewCanvas
                          layout={themePreviewLayout}
                          theme={themePreviewRuntime}
                          height={320}
                          zoomMultiplier={1.55}
                        />
                        <Text size="xs" c="gray.5">
                          This preview applies wallpaper and floor colors
                          separately.
                        </Text>
                      </Stack>

                      <Group justify="space-between">
                        <Button
                          variant="light"
                          onClick={() => setSelectedThemeId("new")}
                        >
                          New Theme
                        </Button>
                        <Button
                          loading={savingTheme}
                          onClick={() => void saveTheme()}
                        >
                          {selectedThemeId === "new"
                            ? "Create Theme"
                            : "Save Theme"}
                        </Button>
                      </Group>
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    </Box>
  );
}
